/**
 * Utility functions for timezone-aware time formatting
 */

/**
 * Format a date/time in the specified timezone
 * @param date - Date to format
 * @param timezone - Timezone string (e.g., 'America/New_York')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatTimeInTimezone = (
  date: Date | string | number,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  }
): string => {
  try {
    const dateObj = new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: timezone
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting time in timezone:', error);
    // Fallback to local time if timezone is invalid
    return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
  }
};

/**
 * Format current time in the specified timezone
 * @param timezone - Timezone string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted current time string
 */
export const formatCurrentTimeInTimezone = (
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  }
): string => {
  return formatTimeInTimezone(new Date(), timezone, options);
};

/**
 * Format a date/time for display (shorter format)
 * @param date - Date to format
 * @param timezone - Timezone string
 * @returns Formatted date string
 */
export const formatDisplayTime = (
  date: Date | string | number,
  timezone: string
): string => {
  return formatTimeInTimezone(date, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

/**
 * Format a date for display (date only)
 * @param date - Date to format
 * @param timezone - Timezone string
 * @returns Formatted date string
 */
export const formatDisplayDate = (
  date: Date | string | number,
  timezone: string
): string => {
  return formatTimeInTimezone(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST")
 * @param timezone - Timezone string
 * @returns Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const timezonePart = parts.find(part => part.type === 'timeZoneName');
    return timezonePart?.value || timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
};
